// Uneltele asistentului pe ECRANUL DE COSTURI. Citesc rezumatul de pe ecran și
// cer serverului (`api/business_case.py`, aceeași socoteală ca pagina) ce nu e
// pe ecran: celelalte scenarii, o simulare — o a doua implementare a socotelii
// ar fi dat altă cifră decât cea de pe ecran la prima regulă schimbată într-un
// singur loc.
//
// NU SCHIMBĂ SCENARIUL OMULUI. Un «și dacă vindem cu 10 % mai scump?» devine un
// scenariu NOU, făcut din cel curent, cu un nume care spune ce e altfel, iar
// pagina trece pe el (`creeaza_scenariu`, Stefan 28.10.2026) — cel vechi rămâne
// în listă, iar «Scenarii» îl pune înapoi dintr-un clic. `simuleaza` rămâne
// pentru socotelile care nu sunt o variantă de păstrat (căutarea unui prag).
import type { DevizFatada } from "../api";
import type { EstimareStructura } from "../structure/tipuri";
import { CHEI_AJUSTABILE, DECIZII, DIN_PROGRAM, DIN_PROIECT, type Ipoteze, type Rezumat } from "../viewer/cost/businessCase";
import type { Program, RezultatOptimizare, SetariProgram, TintaOptimizare } from "../viewer/cost/program";
import { dinServer, type RaspunsBusinessCase, type RezumatServer } from "../viewer/cost/jetoane";
import type { Abateri, Scenariu, StareScenarii } from "../viewer/cost/scenarii";
import type { Rezultat, Unealta } from "./unelte";
import type { OrganizareSantier } from "../viewer/timelapse/santier";
import { camioaneleLa, fazaLa, macaraleleLa } from "../viewer/timelapse/santier";
import { cheia, iso, saptamanile, stareaSaptamanii } from "../viewer/timelapse/stare";

/** Ce știe pagina de costuri — citit la fiecare apel, ca o unealtă să vadă ce
 *  e pe ecran ACUM, nu ce era la deschiderea discuției. */
export type ContextCosturi = {
  stare: () => {
    /** faptele pe care s-a făcut rezumatul (răspunsul serverului); `null` până sosește */
    proiect: RaspunsBusinessCase["proiect"] | null;
    /** rezumatul scenariului curent, cel de pe ecran; `null` până sosește */
    rez: Rezumat | null;
    scenarii: StareScenarii | null;
    curent: Scenariu;
    estimare: EstimareStructura | null;
    devizFatada: DevizFatada | null;
  };
  /** Rezumatul scenariului `s`, de la server, pe lista și programul lui — un
   *  scenariu cu prețurile unei oferte are lista lui. Lipsă = numai cel curent. */
  rezumatPentru?: (s: Scenariu) => Promise<RezumatServer | null>;
  /** Programul de execuție al scenariului `s` (al listei lui), de la server;
   *  `null` fără listă sau cât se calculează. Lipsă = pagina n-are program. */
  program?: (s: Scenariu) => Promise<Program | null>;
  /** Organizarea de șantier a scenariului `s` (documentul planificării:
   *  fazele, gardul și porțile, barăcile, macaralele așezate, camioanele,
   *  mutările); `null` fără plan de situație sau cât se calculează. */
  santier?: (s: Scenariu) => Promise<OrganizareSantier | null>;
  /** Rezumatul scenariului curent cu abaterile date peste, FĂRĂ să salveze. */
  simuleaza: (abateri: Abateri) => Promise<RaspunsBusinessCase>;
  /** `null` pe un proiect numai de citit; `program` = suprascrierile de program
   *  ale scenariului nou (macarale pe perioade, efective, lag-uri…). */
  creeaza: ((abateri: Abateri, meta: { nume: string; autor: "asistent"; nota?: string; program?: SetariProgram })
    => Promise<Scenariu>) | null;
  alege: ((id: string) => Promise<void>) | null;
  /** Optimizatorul programului (`POST …/program/optimizeaza`): ținta → pârghiile
   *  găsite, traiectoria, refuzurile cu motiv; cu `program` și o țintă deja
   *  atinsă e o simulare pe setările date. Lipsă = pagina n-are program. */
  optimizeaza?: (body: { tinta: TintaOptimizare; scenariu?: string | null; program?: SetariProgram;
                         permise?: string[]; pasi_max?: number }) => Promise<RezultatOptimizare>;
};

const r0 = (v: number) => Math.round(v);
/** Scenariul schimbă ceva în program sau în rețete (altfel programul e al bazei). */
const areProgramul = (s: { program?: SetariProgram | null; catalog?: unknown }) =>
  !!(s.program && Object.keys(s.program).length) || !!(s.catalog && Object.keys(s.catalog as object).length);
const r2 = (v: number) => Math.round(v * 100) / 100;
const r4 = (v: number) => Math.round(v * 10000) / 10000;

/** Cifrele de decizie ale unui rezumat, rotunjite cât să se citească. */
/** PE CE STĂ RÂNDUL «STRUCTURĂ» al rezumatului — spus în fiecare răspuns al
 *  uneltelor. Devizul de detaliu se încarcă după ce pagina s-a deschis (rece,
 *  zeci de secunde pe un ansamblu); până sosește, rândul stă pe reperul pe m²,
 *  iar cifrele unui apel de dinainte și ale unuia de după diferă cu toată
 *  structura. Fără eticheta asta, asistentul le lua drept o neconcordanță
 *  între surse (Stefan, 28.10.2026). */
function bazaStructurii(p: RaspunsBusinessCase["proiect"] | null): string {
  if (p?.are_deviz_structura) return "devizul calculului de detaliu al structurii";
  if (p?.structuraInLucru) return "reper pe m² — structura se calculează acum; cifrele se schimbă când e gata";
  return "reper pe m² — devizul de detaliu al structurii nu e încărcat (încă se citește sau lipsește); "
    + "cifrele se schimbă când sosește";
}

function cifre(r: Rezumat) {
  return {
    tic: r0(r.tic), tic_pe_m2_construit: r2(r.ticPeMp), constructii: r0(r.constructii),
    venituri: r0(r.venituri), profit_brut: r0(r.profitBrut), profit_net: r0(r.profitNet),
    marja: r4(r.marja), roi: r4(r.roi), roi_pe_an: r4(r.roiPeAn),
    prag_eur_m2_util: r2(r.pragRentabilitate),
    // banii în timp: capitalul maxim imobilizat, capital-anii și RIR, pe programul de execuție
    capital_max: r.capitalMax != null ? r0(r.capitalMax) : null, capital_ani: r.capitalAni != null ? r0(r.capitalAni) : null,
    rir: r.rir != null ? r4(r.rir) : null,
  };
}

const NUMERICE = (i: Ipoteze) => Object.keys(i).filter((k) =>
  typeof (i as Record<string, unknown>)[k] === "number");

/** Durata scenariului, cum o vede modelul: lunile programului când există unul
 *  (și atunci nu e ipoteză), altfel anii asumați. */
function durata(i: Ipoteze, luniProgram: number | null | undefined) {
  return luniProgram
    ? { luni: r2(luniProgram), ani: r2(i.duratiAni), sursa: "program",
        se_schimba: "numai prin program: creeaza_scenariu cu `program`",
        roi_pe_an_pe: "capitalul imobilizat: cheltuielile se decontează lunar după program, profit net / capital-ani" }
    : { luni: r2(i.duratiAni * 12), ani: r2(i.duratiAni), sursa: "ipoteza", se_schimba: "ipoteza duratiAni",
        roi_pe_an_pe: "costul total × durata asumată (toți banii la început)" };
}

/** Setările de program pe care un scenariu le poate suprascrie (`api/program.py`
 *  le verifică valorile); orice altă cheie e o greșeală pe care modelul o citește. */
const SETARI_PROGRAM_PERMISE = ["start", "zile_lucratoare", "ore_pe_zi", "productivitate", "ciclu_min_zl",
  "ciclu_min_subsol_zl", "marja_zl", "ore_macara_m3", "amprenta_pe_macara_m2", "macarale_max_santier",
  "macarale_perioade", "m2_pe_macara_zi", "densitate_m2_om", "zile_fundatii_zl", "muncitori", "lag_zile",
  "ordinea_corpurilor", "material_structura",
  // pârghiile ([{id, valoare?}]) și parametrii restricțiilor pe care le mișcă
  "parghii", "pompa_beton", "cofraje_rapide", "lift_santier", "schimburi_structura", "beton_statie_m3_zi",
  "pompa_m3_zi", "brat_macara_m"] as const;

function programDin(arg: Record<string, unknown> | undefined): SetariProgram | null | string {
  if (arg == null) return null;
  if (typeof arg !== "object" || Array.isArray(arg)) return "`program` trebuie să fie un obiect cu setări";
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(arg)) {
    if (!(SETARI_PROGRAM_PERMISE as readonly string[]).includes(k)) {
      return `setare de program necunoscută: «${k}» (se pot schimba: ${SETARI_PROGRAM_PERMISE.join(", ")})`;
    }
    if (v != null) out[k] = v;
  }
  return Object.keys(out).length ? (out as SetariProgram) : null;
}

/** Programul, cât îi trebuie modelului ca să spună unde se duce timpul: lanțul
 *  critic, fiecare corp cu ciclul lui și ce îl dă, macaralele cu așteptările,
 *  meseriile, explicațiile — nu pachetele, nici histograma. */
function programPeScurt(p: Program) {
  const corpuri = p.corpuri.map((c) => {
    const m = p.macarale.pe_corp[c.cod] ?? c.macarale;
    const cicluri = c.niveluri.flatMap((n) => (n.ciclu ? [n.ciclu] : []));
    const ceDaRitmul: Record<string, number> = {};
    for (const x of cicluri) ceDaRitmul[x.da_ritmul] = (ceDaRitmul[x.da_ritmul] ?? 0) + 1;
    return {
      corp: c.cod, niveluri: c.niveluri.length,
      structura: m?.structura ? { start: m.structura.start, sfarsit: m.structura.sfarsit } : null,
      sfarsit: c.etape.reduce((a, e) => (e.sfarsit > a ? e.sfarsit : a), ""),
      ciclu_zile_mediu: cicluri.length ? r2(cicluri.reduce((a, x) => a + x.zile, 0) / cicluri.length) : null,
      ciclu_zile_max: cicluri.length ? Math.max(...cicluri.map((x) => x.zile)) : null,
      // câte niveluri și-au luat ciclul de la fiecare limită
      ce_da_ritmul: ceDaRitmul,
      plafon_oameni_pe_nivel: c.niveluri.find((n) => n.plafon_oameni != null)?.plafon_oameni ?? null,
      // regulile care au dat ciclul nivelurilor și startul corpului (vezi `reguli`)
      limitat_de: Array.from(new Set([...cicluri.flatMap((x) => x.limitat_de ?? []), ...(m?.limitat_de ?? [])])),
      macarale: m ? { buc: m.buc, de_la: m.start, pana_la: m.sfarsit, luni_macara: r2(m.luni_macara),
                      asteptat_zile: m.asteptat_zile, plafon_la_start: m.plafon_la_start,
                      eliberat_de: m.eliberat_de, pornit_pe_subsolul: m.subsol ?? null } : null,
    };
  });
  // subsolurile comune: o lucrare a lor, înaintea blocurilor de pe ele
  const subsoluri = (p.subsoluri ?? []).map((b) => ({
    subsol: b.cod, blocuri_pe_el: b.corpuri, niveluri: b.niveluri.length,
    structura: b.macarale?.structura ? { start: b.macarale.structura.start, sfarsit: b.macarale.structura.sfarsit } : null,
    macarale: b.macarale ? { buc: b.macarale.buc, luni_macara: r2(b.macarale.luni_macara) } : null,
    lucrari: [...b.etape, ...b.niveluri.flatMap((n) => n.etape)].filter((e) => e.zi_sfarsit > e.zi_start)
      .map((e) => ({ etapa: e.etapa, nivel: e.nivel, start: e.start, sfarsit: e.sfarsit })),
  }));
  return {
    luni: r2(p.luni), zile_lucratoare: p.zile_lucratoare, start: p.start, sfarsit: p.sfarsit,
    lant_critic: p.etape.filter((e) => e.ritm).map((e) => ({
      etapa: e.cod, nume: e.nume, start: e.start, sfarsit: e.sfarsit, unitati: e.unitati })),
    etape: p.etape.map((e) => ({
      etapa: e.cod, nume: e.nume, start: e.start, sfarsit: e.sfarsit, lag_zile: e.lag_zile,
      decalaj_final_zile: e.decalaj_final_zile, muncitori: e.muncitori })),
    macarale: { plafon: p.macarale.plafon, perioade: p.macarale.perioade, fronturi_max: p.macarale.fronturi_max,
                max_in_structura: p.macarale.max_in_structura, max_simultan: p.macarale.max_simultan,
                luni_macara: r2(p.macarale.luni_macara) },
    subsoluri,
    corpuri,
    meserii: Object.entries(p.meserii).map(([cod, m]) => ({
      cod, nume: m.nume, recomandat: m.recomandat, folosit: m.folosit, varf: m.varf,
      utilizare_pct: m.utilizare_pct, zile_pe_santier: m.zile_pe_santier })),
    muncitori_varf: p.muncitori_varf,
    setari: { ciclu_min_zl: p.setari.ciclu_min_zl, ciclu_min_subsol_zl: p.setari.ciclu_min_subsol_zl,
              marja_zl: p.setari.marja_zl, densitate_m2_om: p.setari.densitate_m2_om,
              macarale_max_santier: p.setari.macarale_max_santier, ore_pe_zi: p.setari.ore_pe_zi,
              zile_lucratoare: p.setari.zile_lucratoare, lag_zile: p.setari.lag_zile, muncitori: p.setari.muncitori },
    explicatii: p.explicatii, avertismente: p.avertismente,
    // regulile cu parametrii efectivi și DE CE există: cu ele se explică o limitare
    reguli: (p.reguli ?? []).map((r) => ({ id: r.id, categorie: r.categorie, activa: r.activa,
                                            parametri: r.parametri, nume: r.nume, explicatie: r.explicatie })),
    material_structura: p.setari.material_structura ?? "beton",
    ce_inseamna_da_ritmul: { minim: "ciclul minim: întărirea și decofrarea betonului",
                             macara: "cât poate ridica macaraua corpului",
                             beton: "cât beton se poate turna pe zi (pompa sau stația)",
                             echipa: "echipa structurii care încape pe nivel (densitatea)",
                             urmasi: "o meserie care urmează n-ar încăpea pe nivel la un ritm mai mare" },
    // RESTRICȚIILE fizice (cu care e atinsă), PÂRGHIILE (aplicate, disponibile,
    // refuzate cu motivul, fiecare cu costul și riscul) și COSTURILE a ce e activ:
    // de aici se spune ce se poate și de ce nu se poate
    restrictii: (p.restrictii ?? []).map((r) => ({ id: r.id, nume: r.nume, activa: r.activa, atinsa: r.atinsa,
                                                    valoare: r.valoare, limita: r.limita, um: r.um, unde: r.unde,
                                                    explicatie: r.explicatie, de_ce_atinsa: r.atinsa_text })),
    restrictii_atinse: p.restrictii_atinse ?? [],
    parghii: (p.parghii ?? []).map((x) => ({ id: x.id, nume: x.nume, aplicata: !!x.aplicata, valoare_aplicata: x.valoare_aplicata ?? null,
                                              disponibila: !!x.disponibila, motiv_refuz: x.motiv_refuz ?? null,
                                              limita_neverificata: !!x.limita_neverificata,
                                              valoare: x.valoare, ce_face: x.explicatie, cost: x.cost, risc: x.risc })),
    costuri_suplimentare: (p.costuri_suplimentare ?? []).map((c) => ({ id: c.id, eur: r0(c.eur), explicatie: c.explicatie })),
    cost_suplimentar_eur: r0(p.cost_suplimentar_eur ?? 0),
    cost_macarale: p.cost_macarale ? { eur: r0(p.cost_macarale.eur), luni_macara: r2(p.cost_macarale.luni_macara),
                                       buc: p.cost_macarale.buc, nota: "e deja în listă (S.MAC)" } : null,
    ce_se_poate_schimba: "creeaza_scenariu cu `program`: PÂRGHIILE `parghii: [{id, valoare?}]` (din `parghii` de mai sus, "
      + "numai cele disponibile; fiecare cu costul ei, care intră în business case), macarale_perioade (plafon mai "
      + "mare o vreme → un corp în plus pornește), macarale_max_santier, muncitori, lag_zile, ciclu_min_zl, marja_zl, "
      + "densitate_m2_om, ore_pe_zi, zile_lucratoare, start, ordinea_corpurilor, material_structura; o pârghie "
      + "refuzată se spune cu motivul ei, nu se forțează prin setări",
  };
}

/** Un punct al traiectoriei optimizatorului, cât îi trebuie modelului. */
function punct(x: RezultatOptimizare["traiectorie"][number]) {
  return {
    luni: r2(x.luni), sfarsit: x.sfarsit, cost_parghii_eur: r0(x.cost_suplimentar_eur), cost_macarale_eur: r0(x.cost_macarale_eur),
    bani_in_plus_fata_de_acum: r0(x.cost_extra_eur), restrictii_atinse: x.restrictii_atinse,
    ...(x.roi_anual != null ? { roi_anual: r4(x.roi_anual), tic: x.tic != null ? r0(x.tic) : null,
                                profit_net: x.profit_net != null ? r0(x.profit_net) : null,
                                capital_max: x.capital_max != null ? r0(x.capital_max) : null,
                                capital_ani: x.capital_ani != null ? r0(x.capital_ani) : null,
                                rir: x.rir != null ? r4(x.rir) : null } : {}),
  };
}

/** Rezultatul optimizatorului pe scurt: planul cu numele pârghiilor, drumul,
 *  ce n-a intrat și de ce, setările de pus pe scenariu. */
function optimizarePeScurt(r: RezultatOptimizare) {
  const nume = (id: string) => r.parghii.find((x) => x.id === id)?.nume ?? id;
  return {
    mod: r.mod, tinta_luni: r.tinta_luni, atins: r.atins,
    inainte: punct(r.baza), dupa: punct(r.rezultat),
    plan: r.pasi.map((p) => ({ pas: p.pas, parghie: p.parghie, nume: nume(p.parghie), valoare: p.valoare,
                               luni_castigate: p.d_luni, bani_in_plus: r0(p.d_cost), roi_anual_plus: r4(p.d_roi) })),
    traiectorie: r.traiectorie.map((x) => ({ pas: x.pas, parghie: x.parghie, ...punct(x) })),
    // de pus pe scenariu: `creeaza_scenariu` cu `program: {parghii}`
    program_pentru_scenariu: { parghii: r.setari.parghii ?? [] },
    refuzate: r.refuzate.map((x) => ({ id: x.id, nume: x.nume, motiv: x.motiv })),
    fara_castig: r.fara_castig.map((x) => ({ id: x.id, nume: nume(x.id), luni: x.d_luni, bani: r0(x.d_cost), roi_anual: r4(x.d_roi) })),
    parghii: r.parghii.map((x) => ({ id: x.id, nume: x.nume, aplicata: !!x.aplicata, disponibila: !!x.disponibila,
                                     motiv_refuz: x.motiv_refuz ?? null, cost: x.cost, risc: x.risc })),
    costuri_suplimentare: r.costuri_suplimentare.map((c) => ({ id: c.id, eur: r0(c.eur), explicatie: c.explicatie })),
    restrictii_atinse: r.restrictii.filter((x) => x.atinsa).map((x) => ({ id: x.id, nume: x.nume, de_ce: x.atinsa_text })),
    explicatii: r.explicatii, avertismente: r.avertismente, note: r.note ?? [],
  };
}

/** Abaterile cerute de model, verificate: chei cunoscute, nimic din proiect,
 *  factori în [0, 10]. Întoarce greșeala ca text, pe care modelul o citește. */
function abateriDin(arg: { ipoteze?: Record<string, number>; ipoteze_relative?: Record<string, number>;
                           ajustari?: Record<string, number> },
                    i: Ipoteze, luniProgram?: number | null): Abateri | string {
  const out: Abateri = {};
  const permise = NUMERICE(i);
  // RELATIVE («cu 10 % mai scump») = factor pe valoarea scenariului curent: modelul
  // n-are de ce să înmulțească în cap 2 250 × 1,1
  const relative: Record<string, number> = {};
  for (const [k, f] of Object.entries(arg.ipoteze_relative ?? {})) {
    if (typeof f !== "number" || !Number.isFinite(f) || f < 0 || f > 10) {
      return `«${k}»: factor între 0 și 10 (1,1 = +10 %)`;
    }
    if (!permise.includes(k)) return `ipoteză necunoscută: «${k}»`;
    relative[k] = (i as unknown as Record<string, number>)[k] * f;
  }
  for (const [k, v] of Object.entries({ ...relative, ...(arg.ipoteze ?? {}) })) {
    if ((DIN_PROIECT as readonly string[]).includes(k)) {
      return `«${k}» vine din proiect (planul de situație, actele); un scenariu nu-l poate schimba`;
    }
    // DURATA E A PROGRAMULUI cât există unul: «mai repede» = alt program, nu altă cifră
    if (luniProgram && (DIN_PROGRAM as readonly string[]).includes(k)) {
      return `«${k}» vine din programul de execuție (${r2(luniProgram)} luni) și nu se schimbă din ipoteze; `
        + "durata se scurtează prin program: citește-l cu `citeste_programul`, apoi `creeaza_scenariu` cu "
        + "`program` (macarale_perioade, muncitori, lag_zile…)";
    }
    if (!permise.includes(k)) return `ipoteză necunoscută: «${k}»`;
    if (typeof v !== "number" || !Number.isFinite(v) || v < 0) return `«${k}»: număr pozitiv`;
    (out as Record<string, number>)[k] = v;
  }
  const aj: Record<string, number> = {};
  for (const [k, v] of Object.entries(arg.ajustari ?? {})) {
    if (!(CHEI_AJUSTABILE as readonly string[]).includes(k)) return `rând de cost neajustabil: «${k}»`;
    if (typeof v !== "number" || !Number.isFinite(v) || v < 0 || v > 10) {
      return `«${k}»: factor între 0 și 10 (1,1 = +10 %)`;
    }
    aj[k] = v;
  }
  if (Object.keys(aj).length) out.ajustari = aj;
  return out;
}


/** Rezumatul ca TABEL (`campuri` o dată, apoi `randuri`): un obiect pe rând ar
 *  fi repetat numele cheilor de o sută de ori, recitite la fiecare tur. */
function tabel(r: Rezumat, sectiuni?: string[], explicatii = true) {
  const campuri = ["sectiune", "cheie", "element", "cantitate", "um", "parametru", "valoare_eur",
    "sursa", "ajustare", "semnal", ...(explicatii ? ["de_unde", "cum_iese"] : [])];
  const randuri = r.sectiuni.filter((s) => !sectiuni?.length || sectiuni.includes(s.cheie))
    .flatMap((s) => [...s.randuri, ...(s.total ? [s.total] : [])].map((x) => [
      s.cheie, x.cheie, x.element, x.cantitate != null ? r4(x.cantitate) : null, x.um ?? null,
      x.parametru ?? null, x.valoare != null ? r0(x.valoare) : null, x.sursa,
      x.ajustare ?? null, x.semnal ?? null, ...(explicatii ? [x.deUnde, x.cumIese] : []),
    ]));
  return { campuri, randuri };
}

export function unelteCosturi(ctx: ContextCosturi): Unealta[] {
  const acum = () => {
    const s = ctx.stare();
    return { ...s, i: s.rez?.ipoteze ?? null, bazaStructurii: bazaStructurii(s.proiect) };
  };
  const faraRezumat: Rezultat = {
    eroare: "rezumatul de business case nu s-a încărcat încă (serverul îl calculează); încearcă iar peste câteva secunde",
  };

  const unelte: Unealta[] = [
    {
      nume: "citeste_scenariile",
      nucleu: true,
      descriere: "Scenariul curent (abaterile lui și ipotezele de decizie), dacă ipotezele sunt "
        + "confirmate (toate, `verificate`, sau pe rând, `validate`), și lista tuturor scenariilor proiectului cu cifrele lor: cost total (tic), "
        + "venituri, profit net, ROI pe an, prag. Plus numele cheilor de ipoteze (`decizii`, "
        + "`toate_ipotezele`) și ale rândurilor ajustabile (`randuri_ajustabile`). Începe cu ea.",
      parametri: { type: "object", properties: {} },
      ruleaza: async (): Promise<Rezultat> => {
        const { scenarii, curent, i, rez, bazaStructurii, proiect } = acum();
        if (!rez || !i) return faraRezumat;
        const lista = scenarii?.scenarii.length ? scenarii.scenarii : [curent];
        // FIECARE PE LISTA LUI: serverul socotește fiecare scenariu pe lista și
        // programul lui; pe lista celui curent, un scenariu cu prețurile unei
        // oferte ieșea cu cifrele bazei
        const rezumate = await Promise.all(lista.map(async (s): Promise<Rezumat | null> => {
          if (s.id === curent.id || !ctx.rezumatPentru) return rez;
          const r = await ctx.rezumatPentru(s);
          return r ? dinServer(r) : null;
        }));
        return {
          continut: {
            curent: { id: curent.id, nume: curent.nume, abateri: curent.ipoteze, cifre: cifre(rez),
                      durata: durata(i, proiect?.programLuni) },
            baza_structurii: bazaStructurii,
            verificate: scenarii?.verificate ?? null,
            validate: scenarii?.validate ?? {},
            decizii: Object.fromEntries(DECIZII.map((k) => [k, i[k]])),
            toate_ipotezele: Object.fromEntries(NUMERICE(i).map((k) => [k, (i as unknown as Record<string, number>)[k]])),
            din_proiect_nu_se_schimba: DIN_PROIECT,
            din_program_nu_se_schimba: proiect?.programLuni ? DIN_PROGRAM : [],
            randuri_ajustabile: CHEI_AJUSTABILE,
            scenarii: lista.map((s, k) => ({
              id: s.id, nume: s.nume, autor: s.autor, parinte: s.parinte ?? null, nota: s.nota ?? null,
              abateri: s.ipoteze, ...(rezumate[k] ? cifre(rezumate[k]!) : { cifre: null }),
            })),
          },
          rezumat: `${lista.length} scenarii`,
        };
      },
    },
    {
      nume: "citeste_programul",
      nucleu: true,
      descriere: "Programul de execuție al scenariului curent (sau al celui dat după `scenariu`): durata în "
        + "luni, începutul și sfârșitul, lanțul critic (etapele care dau durata), fiecare corp cu structura lui, "
        + "ciclul unui nivel și CE îl dă (întărirea, macaraua, echipa care încape pe nivel, meseriile care "
        + "urmează), macaralele lui, cât a așteptat după macarale și cine le-a eliberat, plafonul de macarale "
        + "cu perioadele lui, meseriile cu efectivul recomandat/folosit și utilizarea, explicațiile și "
        + "avertismentele programului, setările care se pot schimba, și REGULILE de planificare cu parametrii "
        + "lor și de ce există (`reguli`; `limitat_de` pe corp spune care l-au ținut). Durata business case-ului vine de aici; "
        + "«mai repede» înseamnă alt program (`creeaza_scenariu` cu `program`), nu altă cifră.",
      parametri: { type: "object", properties: {
        scenariu: { type: "string", description: "id-ul unui scenariu; lipsă = cel curent" } } },
      ruleaza: async (arg: { scenariu?: string }): Promise<Rezultat> => {
        if (!ctx.program) return { eroare: "pagina n-are program de execuție (proiectul n-are listă de cantități)" };
        const { scenarii, curent } = acum();
        const s = arg.scenariu ? (scenarii?.scenarii.find((x) => x.id === arg.scenariu) ?? null) : curent;
        if (!s) return { eroare: `scenariu necunoscut: «${arg.scenariu}»` };
        const p = await ctx.program(s);
        if (!p) {
          return { eroare: "programul nu s-a calculat încă (lista de cantități lipsește sau se calculează); "
            + "încearcă iar peste câteva secunde" };
        }
        return { continut: programPeScurt(p), rezumat: `${r2(p.luni)} luni` };
      },
    },
    {
      nume: "citeste_santierul",
      descriere: "ORGANIZAREA DE ȘANTIER a scenariului curent (sau a celui dat după `scenariu`), documentul "
        + "planificării calculat din planul de situație, program și norme: fazele (de la ce jalon al programului la "
        + "care, cu efectivul de vârf, curtea, containerele pe feluri, etajate sau nu, mutarea de la începutul fazei), "
        + "poarta de camioane (pe ce stradă, de ce), spălarea, drumul provizoriu, MACARALELE așezate (pe ce unitate, "
        + "fereastra, unde stau — în groapă / pe placă / în curte, cât acoperă, brațul peste vecini), câte catarge "
        + "ÎNCAP pe lot față de plafonul programului, direcția săpăturii fiecărui subsol, camioanele pe zi la vârf și "
        + "sursa volumelor, mutările, AVERTISMENTELE și explicațiile. «Încape o macara în plus?» se judecă pe "
        + "`incap`; «mută poarta» nu se poate încă (pozițiile nu se impun de mână).",
      parametri: { type: "object", properties: {
        scenariu: { type: "string", description: "id-ul unui scenariu; lipsă = cel curent" } } },
      ruleaza: async (arg: { scenariu?: string }): Promise<Rezultat> => {
        if (!ctx.santier) return { eroare: "pagina n-are organizare de șantier (proiectul n-are listă de cantități)" };
        const { scenarii, curent } = acum();
        const s = arg.scenariu ? (scenarii?.scenarii.find((x) => x.id === arg.scenariu) ?? null) : curent;
        if (!s) return { eroare: `scenariu necunoscut: «${arg.scenariu}»` };
        const d = await ctx.santier(s);
        if (!d) return { eroare: "organizarea de șantier nu s-a calculat (fără plan de situație în fir, sau se calculează); încearcă iar peste câteva secunde" };
        return { continut: santierPeScurt(d), rezumat: `${d.faze.length} faze, ${d.macarale.length} macarale` };
      },
    },
    {
      nume: "frontul_saptamanii",
      descriere: "UNDE E FRONTUL într-o săptămână a programului (timelapse-ul în cuvinte): dă `saptamana` (de la 1) sau "
        + "`data` (ISO) și primești lunea săptămânii, pachetele în lucru cu fracția și faza lor (cofraj / armare / beton / "
        + "decofrare / săpătură / fundații), stadiul structurii pe corpuri (niveluri gata, nivelul în lucru), oamenii, "
        + "macaralele cu fereastra deschisă (și, cu organizarea de șantier, unde stau), faza organizării, camioanele pe zi "
        + "și cât din exterioare e făcut. Totul din desfășurarea scrisă de motor; nimic interpolat.",
      parametri: { type: "object", properties: {
        saptamana: { type: "number", description: "săptămâna programului, de la 1" },
        data: { type: "string", description: "o zi (AAAA-LL-ZZ); se ia săptămâna ei" },
        scenariu: { type: "string", description: "id-ul unui scenariu; lipsă = cel curent" } } },
      ruleaza: async (arg: { saptamana?: number; data?: string; scenariu?: string }): Promise<Rezultat> => {
        if (!ctx.program) return { eroare: "pagina n-are program de execuție (proiectul n-are listă de cantități)" };
        const { scenarii, curent } = acum();
        const s = arg.scenariu ? (scenarii?.scenarii.find((x) => x.id === arg.scenariu) ?? null) : curent;
        if (!s) return { eroare: `scenariu necunoscut: «${arg.scenariu}»` };
        const p = await ctx.program(s);
        if (!p) return { eroare: "programul nu s-a calculat încă; încearcă iar peste câteva secunde" };
        const sapt = saptamanile(p);
        let luni: Date | undefined;
        if (arg.data) {
          const d = new Date(arg.data + "T00:00:00");
          if (Number.isNaN(d.getTime())) return { eroare: `data «${arg.data}» nu e AAAA-LL-ZZ` };
          luni = sapt.find((l) => l.getTime() + 7 * 86400000 > d.getTime()) ?? sapt[sapt.length - 1];
        } else {
          const n = Math.round(Number(arg.saptamana));
          if (!n || n < 1 || n > sapt.length) return { eroare: `săptămâna trebuie să fie între 1 și ${sapt.length}` };
          luni = sapt[n - 1];
        }
        const st = stareaSaptamanii(p, luni);
        const k = iso(luni);
        const org = ctx.santier ? await ctx.santier(s) : null;
        const faza = org ? fazaLa(org, k) : null;
        const cam = org ? camioaneleLa(org, k) : null;
        const e99 = p.santier.find((e) => e.etapa === "E99");
        return {
          continut: {
            saptamana: sapt.findIndex((l) => l.getTime() === luni!.getTime()) + 1, luni: k, din: sapt.length,
            oameni: st.oameni, cost_pana_acum_eur: r0(st.cost_eur),
            pachete_in_lucru: st.active.map((e) => {
              const f = st.pachete.get(cheia(e))?.s;
              return { id: e.id, nume: e.nume, corp: e.corp, nivel: e.nivel, de_la: e.start, pana_la: e.sfarsit,
                       fractie: r2(Math.min(f?.f ?? 0, 1)), faza: f?.faza ?? null };
            }),
            structura: Object.fromEntries(Object.entries(st.structura).map(([c, v]) => [c, { niveluri_gata: v.gata,
              in_lucru: v.inLucru ? { nivel: v.inLucru.nivel, fractie: r2(v.inLucru.f), faza: v.inLucru.faza } : null }])),
            macarale: st.macarale,
            macarale_asezate: org ? macaraleleLa(org, k).map((m) => ({ id: m.id, unitate: m.unitate, unde: m.in_groapa ? "in_groapa" : m.pe_placa ? "pe_placa" : "in_curte", raza_m: m.raza_m })) : null,
            organizare: faza ? { faza: faza.id, fel: faza.fel, motiv: faza.motiv, containere: faza.baraci.length, oameni_varf: faza.oameni_varf } : null,
            camioane_pe_zi: cam?.pe_zi ?? null,
            exterioare_fractie: e99 ? r2(Math.min(st.pachete.get(cheia(e99))?.s.f ?? 0, 1)) : null,
            santier_organizat: st.santier,
          },
          rezumat: `săpt. ${sapt.findIndex((l) => l.getTime() === luni!.getTime()) + 1}: ${st.active.length} pachete, ${st.oameni} oameni`,
        };
      },
    },
    {
      nume: "optimizeaza_programul",
      nucleu: true,
      descriere: "OPTIMIZATORUL programului de execuție: pentru o țintă — `mod: \"luni\"` cu `luni` (cel mult "
        + "atâtea luni, cât mai ieftin) sau `mod: \"roi\"` (cel mai bun ROI anual pe capitalul imobilizat) — "
        + "încearcă pârghiile pas cu pas (o macara în plus, pompă, echipe mai mari, zi lungă, sâmbăta, schimbul doi, "
        + "cofraje rapide, prefabricate, lag-uri strânse, marjă mică), pe program ȘI pe business case, și întoarce "
        + "PLANUL (pârghiile în ordinea găsită, fiecare cu lunile câștigate, banii în plus, ROI-ul anual), "
        + "traiectoria, cifrele înainte/după (luni, cost, profit net, ROI anual, RIR), pârghiile REFUZATE cu motivul "
        + "(restricția atinsă) și cele fără câștig, plus `program_pentru_scenariu` de dat lui `creeaza_scenariu`. "
        + "Nu salvează nimic. Durează câteva zeci de secunde.",
      parametri: { type: "object", properties: {
        mod: { type: "string", enum: ["luni", "roi"] },
        luni: { type: "number", description: "ținta în luni, în modul «luni»" },
        scenariu: { type: "string", description: "id-ul scenariului de plecare; lipsă = cel curent" },
        permise: { type: "array", items: { type: "string" }, description: "doar aceste pârghii (id-uri din citeste_programul)" },
      }, required: ["mod"] },
      ruleaza: async (arg: { mod: "luni" | "roi"; luni?: number; scenariu?: string; permise?: string[] }): Promise<Rezultat> => {
        if (!ctx.optimizeaza) return { eroare: "pagina n-are program de execuție (proiectul n-are listă de cantități)" };
        if (arg.mod !== "luni" && arg.mod !== "roi") return { eroare: "`mod` e «luni» sau «roi»" };
        if (arg.mod === "luni" && (typeof arg.luni !== "number" || !(arg.luni > 0))) return { eroare: "în modul «luni» dai `luni` (ținta, > 0)" };
        const { scenarii, curent } = acum();
        const s = arg.scenariu ? (scenarii?.scenarii.find((x) => x.id === arg.scenariu) ?? null) : curent;
        if (!s) return { eroare: `scenariu necunoscut: «${arg.scenariu}»` };
        const tinta: TintaOptimizare = arg.mod === "luni" ? { mod: "luni", luni: arg.luni! } : { mod: "roi" };
        const r = await ctx.optimizeaza({ tinta, scenariu: s.id === curent.id && !areProgramul(s) ? null : s.id,
                                          ...(arg.permise?.length ? { permise: arg.permise } : {}) });
        const o = optimizarePeScurt(r);
        return { continut: o, rezumat: `${o.inainte.luni} → ${o.dupa.luni} luni, ${o.plan.length} pârghii` };
      },
    },
    {
      nume: "simuleaza_programul",
      descriere: "Programul și business case-ul cu alte setări de program sau cu pârghii, FĂRĂ să salveze: "
        + "`program` are aceeași formă ca la `creeaza_scenariu` (`parghii: [{id, valoare?}]`, `macarale_max_santier`, "
        + "`muncitori`…). Întoarce cifrele înainte/după (luni, costul pârghiilor, profit net, ROI anual, RIR), "
        + "restricțiile atinse și costurile suplimentare cu formulele lor.",
      parametri: { type: "object", properties: {
        program: { type: "object", additionalProperties: true },
        scenariu: { type: "string", description: "id-ul scenariului de plecare; lipsă = cel curent" },
      }, required: ["program"] },
      ruleaza: async (arg: { program: Record<string, unknown>; scenariu?: string }): Promise<Rezultat> => {
        if (!ctx.optimizeaza) return { eroare: "pagina n-are program de execuție (proiectul n-are listă de cantități)" };
        const program = programDin(arg.program);
        if (typeof program === "string") return { eroare: program };
        if (!program) return { eroare: "`program` e gol: dă pârghii sau setări" };
        const { scenarii, curent } = acum();
        const s = arg.scenariu ? (scenarii?.scenarii.find((x) => x.id === arg.scenariu) ?? null) : curent;
        if (!s) return { eroare: `scenariu necunoscut: «${arg.scenariu}»` };
        // ținta deja atinsă: optimizatorul întoarce doar baza, pe setările date
        const r = await ctx.optimizeaza({ tinta: { mod: "luni", luni: 10_000 }, program,
                                          scenariu: s.id === curent.id && !areProgramul(s) ? null : s.id, pasi_max: 1 });
        const o = optimizarePeScurt(r);
        return { continut: { program, dupa: o.inainte, restrictii_atinse: o.restrictii_atinse, costuri_suplimentare: o.costuri_suplimentare,
                             parghii: o.parghii, avertismente: o.avertismente },
                 rezumat: `${o.inainte.luni} luni` };
      },
    },
    {
      nume: "citeste_devizul",
      nucleu: true,
      descriere: "Rezumatul de business case al scenariului curent, rând cu rând: cantitate, "
        + "preț/parametru, valoare (EUR), SURSA (acte, puz, proiect = măsurat pe soluții, referinta "
        + "= reper de piață, formula, ipoteza), ajustarea scenariului, semnalele, și — cu "
        + "`explicatii` — de unde vine cifra și cum iese, cu cifrele puse în formulă. Secțiuni: "
        + "ipoteze, cantitati, costuri, vanzari, rezultate.",
      parametri: {
        type: "object",
        properties: {
          sectiuni: { type: "array", items: { type: "string",
            enum: ["ipoteze", "cantitati", "costuri", "vanzari", "rezultate"] } },
          explicatii: { type: "boolean", description: "implicit true" },
        },
      },
      ruleaza: (arg: { sectiuni?: string[]; explicatii?: boolean }): Rezultat => {
        const { rez, bazaStructurii } = acum();
        if (!rez) return faraRezumat;
        const tb = tabel(rez, arg.sectiuni, arg.explicatii !== false);
        return { continut: { ...tb, cifre: cifre(rez), baza_structurii: bazaStructurii },
                 rezumat: `${tb.randuri.length} rânduri` };
      },
    },
    {
      nume: "citeste_articolele",
      descriere: "Devizul de execuție pe articole din spatele unui rând: `terasamente` și "
        + "`structura` (devizul calculului de detaliu al structurii: cantități măsurate × prețuri "
        + "din rețete, material și manoperă separat, cost DIRECT, fără sporuri), `fatada` (devizul "
        + "fațadei). Ca tabel; `corp` restrânge la un tronson; cel mult `max` articole, cele mai "
        + "scumpe întâi.",
      parametri: {
        type: "object",
        properties: {
          capitol: { type: "string", enum: ["terasamente", "structura", "fatada"] },
          corp: { type: "string" },
          max: { type: "integer", minimum: 1, maximum: 300 },
        },
        required: ["capitol"],
      },
      ruleaza: (arg: { capitol: "terasamente" | "structura" | "fatada"; corp?: string; max?: number }): Rezultat => {
        const { estimare, devizFatada } = ctx.stare();
        const max = arg.max ?? 80;
        if (arg.capitol === "fatada") {
          if (!devizFatada) return { eroare: "arhitectura din fir n-are încă deviz de fațadă" };
          const art = devizFatada.articole
            .filter((a) => !arg.corp || (a.corpuri?.[arg.corp] ?? 0) > 0)
            .map((a) => ({ a, tot: a.cantitate * (a.pu_material_eur + a.pu_manopera_eur) }))
            .sort((x, y) => y.tot - x.tot);
          return {
            continut: {
              campuri: ["nr", "capitol", "element", "articol", "um", "cantitate", "pu_material_eur",
                "pu_manopera_eur", "total_eur"],
              randuri: art.slice(0, max).map(({ a, tot }) => [a.nr, a.capitol, a.element, a.articol,
                a.um, r2(a.cantitate), r2(a.pu_material_eur), r2(a.pu_manopera_eur), r0(tot)]),
              articole: art.length, total_direct_eur: r0(devizFatada.total_direct_eur),
              sporuri: devizFatada.indirecte.map((l) => ({ cheie: l.cheie, procent: l.procent })),
              arie_fatada_m2: devizFatada.arie_fatada_m2, nepretuite: devizFatada.nepretuite.length,
            },
            rezumat: `${Math.min(art.length, max)} din ${art.length} articole`,
          };
        }
        if (!estimare) return { eroare: "structura n-are încă deviz de execuție (calculul de detaliu)" };
        const eTeras = (c?: string) => c === "terasamente";
        const art = estimare.articole
          .filter((a) => (arg.capitol === "terasamente") === eTeras(a.capitol_cod))
          .filter((a) => !arg.corp || a.corp === arg.corp)
          .sort((x, y) => y.total_eur - x.total_eur);
        const total = art.reduce((s, a) => s + a.total_eur, 0);
        return {
          continut: {
            campuri: ["nr", "corp", "capitol", "articol", "um", "cantitate", "pu_material_eur",
              "pu_manopera_eur", "total_eur", "precizie", "sursa_pret"],
            randuri: art.slice(0, max).map((a) => [a.nr, a.corp ?? null, a.capitol, a.articol, a.um,
              r2(a.cantitate), r2(a.pu_material_eur), r2(a.pu_manopera_eur), r0(a.total_eur),
              a.precizie, a.sursa_pret]),
            articole: art.length, total_direct_eur: r0(total),
            arie_desfasurata_m2: estimare.arie_desfasurata_m2,
            corpuri: Object.keys(estimare.corpuri ?? {}),
          },
          rezumat: `${Math.min(art.length, max)} din ${art.length} articole`,
        };
      },
    },
    {
      nume: "simuleaza",
      nucleu: true,
      descriere: "Calculează scenariul curent cu alte ipoteze și/sau cu ajustări pe rânduri de "
        + "cost, FĂRĂ să salveze nimic — numai pentru socoteli care nu sunt o variantă de "
        + "păstrat (căutarea unui prag). `ipoteze`: {cheie: valoare} din `toate_ipotezele` "
        + "(procentele ca fracție: cedatDinScd 0.25 = 25 %; prețuri în EUR). `ipoteze_relative`: "
        + "{cheie: factor} pe valoarea de acum (pretApartamenteMp 1.1 = +10 %). `ajustari`: "
        + "{rând: factor} din `randuri_ajustabile` (1.1 = +10 %, 0.9 = −10 %). Întoarce cifrele "
        + "înainte/după, diferența și rândurile care s-au mișcat.",
      parametri: {
        type: "object",
        properties: {
          ipoteze: { type: "object", additionalProperties: { type: "number" } },
          ipoteze_relative: { type: "object", additionalProperties: { type: "number" } },
          ajustari: { type: "object", additionalProperties: { type: "number" } },
        },
      },
      ruleaza: async (arg: { ipoteze?: Record<string, number>; ipoteze_relative?: Record<string, number>;
                             ajustari?: Record<string, number> }): Promise<Rezultat> => {
        const { i, rez, proiect } = acum();
        if (!rez || !i) return faraRezumat;
        const a = abateriDin(arg, i, proiect?.programLuni);
        if (typeof a === "string") return { eroare: a };
        const dupa = dinServer((await ctx.simuleaza(a)).rezumat);
        const ci = cifre(rez), cd = cifre(dupa);
        const vechi = new Map(rez.sectiuni.flatMap((s) => s.randuri).map((x) => [x.cheie, x.valoare]));
        const miscate = dupa.sectiuni.flatMap((s) => s.randuri)
          .filter((x) => x.valoare != null && Math.abs((vechi.get(x.cheie) ?? 0) - x.valoare) > 0.5)
          .map((x) => ({ cheie: x.cheie, element: x.element, inainte: r0(vechi.get(x.cheie) ?? 0),
                         dupa: r0(x.valoare!) }));
        return {
          continut: {
            inainte: ci, dupa: cd,
            diferenta: Object.fromEntries(Object.keys(ci).map((k) =>
              [k, r4((cd as Record<string, number>)[k] - (ci as Record<string, number>)[k])])),
            randuri_miscate: miscate,
          },
          rezumat: `profit net ${r0(ci.profit_net)} → ${r0(cd.profit_net)}`,
        };
      },
    },
    {
      nume: "creeaza_scenariu",
      nucleu: true,
      schimba: true,
      descriere: "Face un scenariu NOU din cel curent, cu abaterile date (aceeași formă ca la "
        + "`simuleaza`: `ipoteze`, `ipoteze_relative`, `ajustari`) și/sau cu alt PROGRAM de execuție "
        + "(`program`: suprascrieri peste setările proiectului — `macarale_perioade` [{de_la, pana_la, maxim}] "
        + "ridică plafonul de macarale o vreme ca să pornească un corp în plus, `macarale_max_santier`, "
        + "`muncitori` {meserie: efectiv}, `lag_zile` {etapă: zile}, `ciclu_min_zl`, `marja_zl`, "
        + "`densitate_m2_om`, `ore_pe_zi`, `zile_lucratoare` [0–4 = luni–vineri], `start` ISO, "
        + "`ordinea_corpurilor`, `material_structura` «beton»/«prefab» — prefabricatele n-au ciclu minim de "
        + "întărire; și PÂRGHIILE `parghii` [{id, valoare?}] din `citeste_programul` — o macara în plus, pompă, "
        + "echipe mai mari, zi lungă, sâmbăta, schimbul doi, cofraje rapide, prefabricate, lag-uri strânse, marjă "
        + "mică —, fiecare cu costul ei care intră în business case; numai cele `disponibila`), îl salvează și "
        + "îl pune curent — pagina trece pe el; cel vechi rămâne "
        + "în listă. `nume`: scurt, în limba omului, spune ce e altfel («Preț de vânzare +10 %», "
        + "«+1 macara în 2028»); `nota` = cererea omului, într-o propoziție. Întoarce cifrele "
        + "înainte/după și, cu program, programul pe scurt înainte/după (luni, corpuri, macarale).",
      parametri: {
        type: "object",
        properties: {
          nume: { type: "string", maxLength: 120 },
          nota: { type: "string", maxLength: 600 },
          ipoteze: { type: "object", additionalProperties: { type: "number" } },
          ipoteze_relative: { type: "object", additionalProperties: { type: "number" } },
          ajustari: { type: "object", additionalProperties: { type: "number" } },
          program: { type: "object", additionalProperties: true },
        },
        required: ["nume"],
      },
      ruleaza: async (arg: { nume: string; nota?: string; ipoteze?: Record<string, number>;
                             ipoteze_relative?: Record<string, number>;
                             ajustari?: Record<string, number>;
                             program?: Record<string, unknown> }): Promise<Rezultat> => {
        if (!ctx.creeaza) return { eroare: "omul are doar drept de citire pe proiect" };
        const { i, rez, proiect, curent } = acum();
        if (!rez || !i) return faraRezumat;
        const a = abateriDin(arg, i, proiect?.programLuni);
        if (typeof a === "string") return { eroare: a };
        const program = programDin(arg.program);
        if (typeof program === "string") return { eroare: program };
        const inainte = program && ctx.program ? await ctx.program(curent) : null;
        const nou = await ctx.creeaza(a, { nume: arg.nume, autor: "asistent", nota: arg.nota,
                                           ...(program ? { program } : {}) });
        const rn = ctx.rezumatPentru ? await ctx.rezumatPentru(nou) : null;
        const dupa = program && ctx.program ? await ctx.program(nou) : null;
        return {
          continut: { id: nou.id, nume: nou.nume, abateri: nou.ipoteze, ...(program ? { program } : {}),
                      inainte: cifre(rez), dupa: rn ? cifre(dinServer(rn)) : null,
                      ...(program ? { program_inainte: inainte ? programPeScurt(inainte) : null,
                                      program_dupa: dupa ? programPeScurt(dupa) : null } : {}) },
          atinse: 1, rezumat: nou.nume,
        };
      },
    },
    {
      nume: "alege_scenariul",
      schimba: true,
      descriere: "Pune curent un scenariu existent (după `id` din `citeste_scenariile`) — "
        + "întoarcerea la o variantă de dinainte. Nu șterge nimic.",
      parametri: { type: "object", properties: { id: { type: "string" } }, required: ["id"] },
      ruleaza: async (arg: { id: string }): Promise<Rezultat> => {
        if (!ctx.alege) return { eroare: "omul are doar drept de citire pe proiect" };
        const { scenarii } = ctx.stare();
        const s = scenarii?.scenarii.find((x) => x.id === arg.id);
        if (!s) return { eroare: `nu există scenariul «${arg.id}»` };
        await ctx.alege(arg.id);
        return { continut: { id: s.id, nume: s.nume }, atinse: 1, rezumat: s.nume };
      },
    },
  ];
  return unelte;
}

/** Rândul fiecărei unelte în panoul asistentului, spus pe înțeles. */
export const PASII_COSTURI: Record<string, { curge: string; gata: string }> = {
  citeste_scenariile: { curge: "costuri.pas.scenarii", gata: "costuri.pas.scenarii_gata" },
  citeste_devizul: { curge: "costuri.pas.deviz", gata: "costuri.pas.deviz_gata" },
  citeste_articolele: { curge: "costuri.pas.articole", gata: "costuri.pas.articole_gata" },
  simuleaza: { curge: "costuri.pas.simuleaza", gata: "costuri.pas.simuleaza_gata" },
  creeaza_scenariu: { curge: "costuri.pas.creeaza", gata: "costuri.pas.creeaza_gata" },
  alege_scenariul: { curge: "costuri.pas.alege", gata: "costuri.pas.alege_gata" },
};


/** Organizarea de șantier, pe scurt, pentru asistent: fazele, accesul,
 *  macaralele așezate, câte încap, săpăturile, camioanele, mutările. */
export function santierPeScurt(d: OrganizareSantier) {
  return {
    start: d.start, sfarsit: d.sfarsit,
    lot: { arie_m2: d.lot.arie_m2, perimetru_m: d.lot.perimetru_m },
    faze: d.faze.map((f) => ({ id: f.id, fel: f.fel, unitate: f.unitate, de_la: f.de_la, pana_la: f.pana_la, motiv: f.motiv,
      oameni_varf: f.oameni_varf, curte_m2: f.curte_m2, subsoluri: f.subsoluri, corpuri_in_lucru: f.corpuri_in_lucru,
      containere: Object.fromEntries(f.containere.map((c) => [c.fel, c.numar])), containere_total: f.baraci.length, etaje_baraci: f.etaje_baraci,
      depozite: f.depozite.map((x) => ({ fel: x.fel, incape: !!x.poly })),
      mutare: f.mutare ? { ce: f.mutare.ce, zile: f.mutare.zile, oameni: f.mutare.oameni } : null })),
    poarta_camioane: d.porti.filter((p) => p.fel === "vehicle").map((p) => ({ strada: p.strada, latime_m: p.latime_m, de_la: p.de_la, pana_la: p.pana_la }))[0] ?? null,
    spalare_roti: { lungime_m: d.spalare_roti.lungime_m, latime_m: d.spalare_roti.latime_m },
    drumuri: d.drumuri.map((r) => ({ id: r.id, latime_m: r.latime_m, sens: r.sens })),
    gard: { lungime_m: d.gard.lungime_m, de_la: d.gard.de_la, pana_la: d.gard.pana_la },
    macarale: d.macarale.map((m) => ({ id: m.id, unitate: m.unitate, fel: m.fel, de_la: m.de_la, pana_la: m.pana_la, raza_m: m.raza_m,
      unde: m.in_groapa ? "in_groapa" : m.pe_placa ? "pe_placa" : "in_curte", acoperire: m.acoperire, rang_brat: m.rang_brat,
      inaltime_carlig_m: m.inaltime_carlig_m, peste_vecini: m.peste_vecini, peste_strada: m.peste_strada })),
    incap: d.incap,
    sapaturi: d.sapaturi.map((s) => ({ unitate: s.unitate, adancime_m: s.adancime_m, arie_m2: s.arie_m2, rampa: s.rampa?.id ?? null, explicatie: s.explicatie })),
    camioane: { varf_pe_zi: d.camioane.varf_pe_zi, surse: d.camioane.surse },
    mutari: d.mutari.map((m) => ({ faza: m.faza, luni: m.luni, zile: m.zile, oameni: m.oameni, ce: m.ce })),
    avertismente: d.avertismente, explicatii: d.explicatii,
    norme: d.norme.map((n) => ({ id: n.id, nivel: n.nivel, sursa: n.sursa })),
  };
}
